import { buildCoverageFeatures } from 'utils/coverageFeaturesUtil';

import {
    RATE_BUCKET_LOW_MAX_GAL_PER_AC,
    RATE_BUCKET_MEDIUM_MAX_GAL_PER_AC
} from 'components/Map/hooks/useFlightPaths/constants';

import {
    BOUNDARY_STROKE_HEX_COLOR,
    DEFAULT_BOUNDARY_STROKE_WIDTH_PIXELS,
    DEFAULT_CANVAS_SIZE_PIXELS,
    RATE_HIGH_HEX_COLOR,
    RATE_LOW_HEX_COLOR,
    RATE_MEDIUM_HEX_COLOR
} from './constants';
import type { FlightResponse, Polygon, RenderRateMapPng } from './types';

export const colorForRate = (rate: number): string => {
    if (rate < RATE_BUCKET_LOW_MAX_GAL_PER_AC) {
        return RATE_LOW_HEX_COLOR;
    }

    if (rate < RATE_BUCKET_MEDIUM_MAX_GAL_PER_AC) {
        return RATE_MEDIUM_HEX_COLOR;
    }

    return RATE_HIGH_HEX_COLOR;
};

// Kept for consumers that need boundary visualization — not called from the
// main render path (transparent PNG export shows coverage polygons only).
export const drawBoundaryStroke = ({
    boundary,
    context,
    projectLatitudeToPixelY,
    projectLongitudeToPixelX
}: {
    // Runtime boundary shape is GeoJSON-style Position[][] (rings of [lng, lat]
    // tuples), not the JTS Coordinate[] the generated Polygon type claims.
    // See utils/geometryExtent for the same mismatch note.
    boundary: Polygon;
    context: CanvasRenderingContext2D;
    projectLatitudeToPixelY: (latitude: number) => number;
    projectLongitudeToPixelX: (longitude: number) => number;
}): void => {
    const rings = (boundary.coordinates ?? []) as unknown as number[][][];

    if (rings.length === 0) {
        return;
    }

    context.strokeStyle = BOUNDARY_STROKE_HEX_COLOR;
    context.lineWidth = DEFAULT_BOUNDARY_STROKE_WIDTH_PIXELS;

    for (const ring of rings) {
        if (!Array.isArray(ring) || ring.length === 0) {
            continue;
        }

        context.beginPath();

        let hasMoved = false;

        for (const point of ring) {
            if (!Array.isArray(point) || point.length < 2) {
                continue;
            }

            const longitude = point[0];
            const latitude = point[1];

            if (typeof longitude !== 'number' || typeof latitude !== 'number') {
                continue;
            }

            const pixelX = projectLongitudeToPixelX(longitude);
            const pixelY = projectLatitudeToPixelY(latitude);

            if (!hasMoved) {
                context.moveTo(pixelX, pixelY);
                hasMoved = true;
                continue;
            }

            context.lineTo(pixelX, pixelY);
        }

        context.closePath();
        context.stroke();
    }
};

const drawCoveragePolygons = ({
    context,
    features,
    projectLatitudeToPixelY,
    projectLongitudeToPixelX
}: {
    context: CanvasRenderingContext2D;
    features: ReturnType<typeof buildCoverageFeatures>['features'];
    projectLatitudeToPixelY: (latitude: number) => number;
    projectLongitudeToPixelX: (longitude: number) => number;
}): void => {
    for (const feature of features) {
        context.fillStyle = colorForRate(feature.properties.rateGalPerAc);
        context.beginPath();

        for (const ring of feature.geometry.coordinates) {
            let hasMoved = false;

            for (const point of ring) {
                const longitude = point[0];
                const latitude = point[1];

                if (typeof longitude !== 'number' || typeof latitude !== 'number') {
                    continue;
                }

                const x = projectLongitudeToPixelX(longitude);
                const y = projectLatitudeToPixelY(latitude);

                if (!hasMoved) {
                    context.moveTo(x, y);
                    hasMoved = true;
                } else {
                    context.lineTo(x, y);
                }
            }

            context.closePath();
        }

        context.fill();
    }
};

const canvasToPngBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
    new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (blob) {
                resolve(blob);

                return;
            }

            reject(new Error('canvas.toBlob returned null'));
        }, 'image/png');
    });

export const renderRateMapPng: RenderRateMapPng = async ({
    canvasSizePixels = DEFAULT_CANVAS_SIZE_PIXELS,
    extent,
    flights,
    telemetryPointsByFlightId
}) => {
    const canvas = document.createElement('canvas');

    canvas.width = canvasSizePixels;
    canvas.height = canvasSizePixels;

    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Failed to acquire 2D canvas context');
    }

    // Canvas is transparent by default — no background fill.

    const longitudeSpan = extent.maximumLongitude - extent.minimumLongitude;
    const latitudeSpan = extent.maximumLatitude - extent.minimumLatitude;

    if (longitudeSpan <= 0 || latitudeSpan <= 0) {
        throw new Error('Cannot render: extent has zero or negative span');
    }

    const projectLongitudeToPixelX = (longitude: number): number =>
        ((longitude - extent.minimumLongitude) / longitudeSpan) * canvasSizePixels;

    const projectLatitudeToPixelY = (latitude: number): number =>
        ((extent.maximumLatitude - latitude) / latitudeSpan) * canvasSizePixels;

    const coverageCollection = buildCoverageFeatures({ flights, telemetryPointsByFlightId });

    drawCoveragePolygons({
        context,
        features: coverageCollection.features,
        projectLatitudeToPixelY,
        projectLongitudeToPixelX
    });

    return canvasToPngBlob(canvas);
};

export type { FlightResponse };
